import { useCallback, useMemo, useRef, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { CustomButton } from "../../../components/common/customButton";
import { uploadDealImage } from "../../../services/dealImagesApi";

const ACCEPTED_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
]);

const ACCEPT_ATTR = ".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp";

function isAcceptedImageFile(file: File): boolean {
  if (ACCEPTED_IMAGE_TYPES.has(file.type)) return true;
  return /\.(png|jpe?g|webp)$/i.test(file.name);
}

function addUniqueFiles(existing: File[], incoming: File[]): File[] {
  const next = [...existing];
  for (const file of incoming) {
    const duplicate = next.some(
      (item) =>
        item.name === file.name &&
        item.size === file.size &&
        item.lastModified === file.lastModified,
    );
    if (!duplicate) next.push(file);
  }
  return next;
}

export interface DealImageUploadDialogProps {
  open: boolean;
  dealId: number;
  onClose: () => void;
  onUploaded: () => void;
}

export default function DealImageUploadDialog({
  open,
  dealId,
  onClose,
  onUploaded,
}: DealImageUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [dragActive, setDragActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canUpload = useMemo(
    () => files.length > 0 && !uploading,
    [files.length, uploading],
  );

  const resetState = () => {
    setFiles([]);
    setCaption("");
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    setDragActive(false);
    setErrorMessage(null);
  };

  const handleClose = () => {
    if (uploading) return;
    resetState();
    onClose();
  };

  const stageFiles = useCallback((fileList: FileList | File[]) => {
    const accepted = Array.from(fileList).filter(isAcceptedImageFile);
    if (accepted.length === 0) {
      setErrorMessage("Please use PNG, JPG, JPEG, or WEBP images.");
      return;
    }
    if (accepted.length < fileList.length) {
      setErrorMessage("Some files were skipped — only image files are allowed.");
    } else {
      setErrorMessage(null);
    }
    setFiles((prev) => addUniqueFiles(prev, accepted));
  }, []);

  const handleChooseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files?.length) {
      stageFiles(event.target.files);
    }
    event.target.value = "";
  };

  const handleDrag = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (uploading) return;
      if (event.type === "dragenter" || event.type === "dragover") {
        setDragActive(true);
      } else if (event.type === "dragleave") {
        setDragActive(false);
      }
    },
    [uploading],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (uploading || !event.dataTransfer.files?.length) return;
      stageFiles(event.dataTransfer.files);
    },
    [stageFiles, uploading],
  );

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setErrorMessage(null);
    setUploadProgress({ current: 0, total: files.length });

    const failures: string[] = [];
    let uploadedCount = 0;

    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index]!;
        setUploadProgress({ current: index + 1, total: files.length });

        const response = await uploadDealImage(dealId, file, {
          caption: files.length === 1 ? caption.trim() || undefined : undefined,
        });

        if (response?.success) {
          uploadedCount += 1;
        } else {
          failures.push(
            `${file.name}: ${
              typeof response?.message === "string"
                ? response.message
                : "Upload failed"
            }`,
          );
        }
      }

      if (uploadedCount > 0) {
        onUploaded();
      }

      if (failures.length === 0) {
        resetState();
        onClose();
        return;
      }

      setErrorMessage(
        uploadedCount > 0
          ? `Uploaded ${uploadedCount} of ${files.length}. ${failures.join(" ")}`
          : failures.join(" "),
      );
      setFiles((prev) =>
        prev.filter((file) =>
          failures.some((failure) => failure.startsWith(`${file.name}:`)),
        ),
      );
    } catch (err) {
      setErrorMessage(
        err instanceof Error ? err.message : "Unexpected error uploading images.",
      );
      if (uploadedCount > 0) {
        onUploaded();
      }
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const uploadButtonLabel = uploading
    ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...`
    : files.length > 1
      ? `Upload ${files.length} images`
      : "Upload";

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pr: 6, fontWeight: 700 }}>
        Upload Images
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={uploading}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept={ACCEPT_ATTR}
          onChange={handleFileChange}
        />

        <Box
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          sx={{
            mt: 1,
            p: 3,
            border: "2px dashed",
            borderColor: dragActive ? "#1B7A63" : "#d1d5db",
            borderRadius: 2,
            backgroundColor: dragActive ? "#eef8f5" : "#fafafa",
            opacity: uploading ? 0.6 : 1,
            textAlign: "center",
            transition: "border-color 0.2s, background-color 0.2s",
          }}
        >
          <CloudUploadOutlinedIcon
            sx={{
              fontSize: 40,
              color: dragActive ? "#1B7A63" : "rgba(27, 122, 99, 0.45)",
              mb: 1,
            }}
          />
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#111827" }}>
            {dragActive ? "Drop images here" : "Drag & drop images here"}
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#55575C", mt: 0.5 }}>
            PNG, JPG, JPEG, or WEBP
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mt: 2,
            flexWrap: "wrap",
          }}
        >
          <CustomButton
            text="Choose Files"
            handleButtonPress={handleChooseFiles}
            solid={false}
            isButtonDisabled={uploading}
            widthOverride="140px"
          />
          <Typography sx={{ fontSize: 14, color: "#55575C" }}>
            {files.length > 0
              ? `${files.length} file${files.length === 1 ? "" : "s"} selected`
              : "Select one or more images"}
          </Typography>
        </Box>

        {files.length > 0 && (
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {files.map((file, index) => (
              <Box
                key={`${file.name}-${file.size}-${file.lastModified}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  border: "1px solid #e5e7eb",
                  borderRadius: 1,
                  backgroundColor: "#fff",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={`Remove ${file.name}`}
                  disabled={uploading}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleRemoveFile(index);
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {files.length === 1 && (
          <TextField
            label="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            fullWidth
            margin="normal"
            disabled={uploading}
          />
        )}

        {uploading && uploadProgress.total > 0 && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              variant="determinate"
              value={(uploadProgress.current / uploadProgress.total) * 100}
            />
          </Box>
        )}

        {errorMessage && (
          <Typography sx={{ mt: 2, fontSize: 14, color: "error.main" }}>
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          text="Cancel"
          handleButtonPress={handleClose}
          solid={false}
          isButtonDisabled={uploading}
        />
        <CustomButton
          text={uploadButtonLabel}
          handleButtonPress={handleUpload}
          solid={true}
          isButtonDisabled={!canUpload}
          widthOverride="160px"
        />
      </DialogActions>
    </Dialog>
  );
}
